import {Signal} from '@angular/core';
import {AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {InheritanceCaseType} from '../models/enums/inheritance-case-type';

export interface FormControlError {
  key: string;
  params?: Record<string, unknown>;
}

export type FormErrorTranslator = (
  key: string,
  params?: Record<string, unknown>
) => string;

const phoneNumberMinDigits = 7;
const phoneNumberMaxDigits = 15;
// Country selects persist German names, while existing billing addresses may contain ISO codes.
const fiveDigitPostalCodeCountries = new Set(['Deutschland', 'Frankreich', 'DE', 'FR']);
const fourDigitPostalCodeCountries = new Set([
  'Österreich',
  'Schweiz',
  'Luxemburg',
  'Belgien',
  'AT',
  'CH',
  'LU',
  'BE',
]);
const netherlandsPostalCodeCountries = new Set(['Niederlande', 'NL']);

type CountryPostalCodeFormat = 'fiveDigits' | 'fourDigits' | 'netherlands' | 'nonBlank';

/**
 * Reads a field value from a signal-backed form value snapshot.
 *
 * This is useful for DocEstate UI components that are bound through `[value]`
 * and `(valueChange)` instead of `formControlName`. The helper keeps templates
 * consistent by normalizing `null` and `undefined` to an empty string.
 *
 * @param valueSignal Signal containing the latest form raw value.
 * @param key Field key to read from the signal value.
 * @returns The field value or an empty string when the value is unset.
 */
export function formValueOf<TValue extends Record<PropertyKey, unknown>, TKey extends keyof TValue>(valueSignal: Signal<TValue>, key: TKey): Exclude<TValue[TKey], null | undefined> | '' {
  return (valueSignal()[key] ?? '') as Exclude<TValue[TKey], null | undefined> | '';
}

/**
 * Writes a UI component value into a reactive form control and marks it as dirty.
 *
 * DocEstate inputs emit values through `(valueChange)` instead of using
 * `formControlName`, so callers need this small bridge whenever a field is
 * bound manually.
 *
 * @param control Control that should receive the emitted value.
 * @param value New value emitted by the UI component.
 */
export function updateFormControlValue<TValue>(control: AbstractControl<TValue>, value: TValue): void {
  control.setValue(value);
  control.markAsDirty();
}

/**
 * Validates that a text control contains at least one non-whitespace character.
 *
 * Angular's built-in `Validators.required` treats strings containing only spaces as present.
 * Use this validator together with `Validators.required` when blank text should be invalid.
 *
 * @param control Form control containing a text-like value.
 * @returns `{blank: true}` when the value is a whitespace-only string, otherwise `null`.
 */
export function nonBlankValidator(
  control: AbstractControl<string | null>
): ValidationErrors | null {
  const value = control.value;

  return typeof value === 'string' && value.trim().length === 0 ? {blank: true} : null;
}

/**
 * Rejects placeholder-like text that consists only of punctuation or symbols.
 *
 * Empty values remain valid so required fields can continue to use the dedicated
 * required and non-blank validators. Real-world text containing at least one
 * Unicode letter or number remains valid, including names with hyphens or apostrophes.
 *
 * @param control Form control containing a text-like value.
 * @returns `{meaningfulText: true}` when no letter or number is present, otherwise `null`.
 */
export function meaningfulTextValidator(control: AbstractControl<string | null>): ValidationErrors | null {
  const value = control.value?.trim();
  if (!value) {
    return null;
  }

  return /[\p{L}\p{N}]/u.test(value) ? null : {meaningfulText: true};
}

/**
 * Validates a personal name consisting of Unicode letters and supported separators.
 *
 * Accented characters and umlauts remain valid. Hyphens as well as straight and typographic
 * apostrophes are accepted between letter groups, for example `Anne-Marie`, `O'Neil`, or
 * `D’Angelo`. Numbers, whitespace, other punctuation, and misplaced separators are rejected.
 * Empty values remain valid so required fields can use the dedicated required validator.
 *
 * @param control Form control containing the text value.
 * @returns `{personName: true}` when the value is not a supported personal name, otherwise `null`.
 */
export function personNameValidator(control: AbstractControl<string | null>,): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null;
  }

  return /^\p{L}+(?:[-'’]\p{L}+)*$/u.test(value) ? null : {personName: true};
}

/**
 * Marks an inheritance case without available documents as invalid.
 *
 * Empty values remain valid so the conditional required validator can handle cases where an
 * inheritance-document selection is required but has not been made yet.
 *
 * @param control Form control containing the selected inheritance-document type.
 * @returns An error when `NONE` is selected, otherwise `null`.
 */
export function inheritanceDocumentsAvailableValidator(control: AbstractControl<InheritanceCaseType | null>,): ValidationErrors | null {
  return control.value === InheritanceCaseType.NONE ? {inheritanceDocumentsUnavailable: true} : null;
}

/**
 * Validates that at least one configured form-group field contains a non-blank value.
 *
 * Use this for groups where multiple alternative fields can satisfy the same requirement,
 * for example a billing address that needs either a person name or a company name.
 *
 * @param fieldNames Form-group control names that should be checked.
 * @returns Validator that reports `{atLeastOneRequired: {fields}}` when all configured fields are empty.
 */
export function atLeastOneNonBlankFieldValidator<TFieldName extends string>(fieldNames: readonly TFieldName[],): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormGroup)) {
      return null;
    }

    const hasValue = fieldNames.some((fieldName) => {
      const value = control.get(fieldName)?.value;

      return typeof value === 'string' ? value.trim().length > 0 : value !== null && value !== undefined;
    });

    return hasValue ? null : {atLeastOneRequired: {fields: fieldNames}};
  };
}

/**
 * Validates a pragmatic international phone number format.
 *
 * The number must start with either a leading `+` (international format) or a leading `0`
 * (national format). After the leading character the validator allows digits, spaces,
 * parentheses, dots, slashes, and hyphens. It also enforces 7 to 15 digits, matching the
 * practical E.164 digit range without forcing users to enter a strict E.164 representation.
 *
 * Empty values are treated as valid so this validator can be combined with
 * `Validators.required` when the field is mandatory.
 *
 * @param control Form control containing the phone number text.
 * @returns `{phoneNumber: {minDigits, maxDigits, actualDigits}}` when the value is invalid, otherwise `null`.
 */
export function phoneNumberValidator(
  control: AbstractControl<string | null>
): ValidationErrors | null {
  const value = control.value?.trim();
  if (!value) {
    return null;
  }

  const usesAllowedCharacters = /^[+0][\d\s().\/-]*$/.test(value);
  const digitCount = value.replace(/\D/g, '').length;

  return usesAllowedCharacters &&
    digitCount >= phoneNumberMinDigits &&
    digitCount <= phoneNumberMaxDigits
    ? null
    : {
        phoneNumber: {
          minDigits: phoneNumberMinDigits,
          maxDigits: phoneNumberMaxDigits,
          actualDigits: digitCount,
        },
      };
}

/**
 * Validates that a house number contains at least one digit.
 *
 * House numbers must include at least one digit but may also contain arbitrary
 * additional characters (for example `12a`, `12-14`, or `12/3`).
 *
 * Empty values remain valid so the validator can be combined with `Validators.required`.
 *
 * @param control Form control containing the house number text.
 * @returns `{houseNumber: true}` when no digit is present, otherwise `null`.
 */
export function houseNumberValidator(control: AbstractControl<string | null>): ValidationErrors | null {
  const value = control.value?.trim();
  if (!value) {
    return null;
  }

  return /\d/.test(value) ? null : {houseNumber: true};
}

/**
 * Validates that an optional text value contains at least one digit.
 *
 * Empty values remain valid so optional fields can use this validator without becoming required.
 *
 * @param control Form control containing the text value.
 * @returns `{containsDigit: true}` when a non-empty value contains no digit, otherwise `null`.
 */
export function containsDigitValidator(control: AbstractControl<string | null>,): ValidationErrors | null {
  const value = control.value?.trim();
  if (!value) {
    return null;
  }

  return /\d/.test(value) ? null : {containsDigit: true};
}

/**
 * Validates a postal code according to the selected country.
 *
 * Country values are the stable German names provided by the country select. Germany and France
 * require five digits; Austria, Switzerland, Luxembourg, and Belgium require four digits; and the
 * Netherlands require four digits followed by one space and two letters. Other countries accept
 * any non-blank value.
 *
 * @param control Postal-code control whose parent group contains the `country` control.
 * @returns A country-specific validation error when the postal code has the wrong format.
 */
export function countryPostalCodeValidator(control: AbstractControl<string | null>,): ValidationErrors | null {
  const value = control.value?.trim() ?? '';
  const country = String(control.parent?.get('country')?.value ?? '').trim();
  let format: CountryPostalCodeFormat;
  let valid: boolean;

  if (fiveDigitPostalCodeCountries.has(country)) {
    format = 'fiveDigits';
    valid = /^\d{5}$/.test(value);
  } else if (fourDigitPostalCodeCountries.has(country)) {
    format = 'fourDigits';
    valid = /^\d{4}$/.test(value);
  } else if (netherlandsPostalCodeCountries.has(country)) {
    format = 'netherlands';
    valid = /^\d{4} [A-Za-z]{2}$/.test(value);
  } else {
    format = 'nonBlank';
    valid = value.length > 0;
  }

  return valid ? null : {countryPostalCode: {format}};
}

/**
 * Validates that an email address contains a complete domain with a suffix.
 *
 * General email syntax remains the responsibility of Angular's `Validators.email`. This
 * additional validator only requires the domain after `@` to contain at least one dot with
 * non-empty labels on both sides, for example `example.de` or `mail.example.com`.
 *
 * Empty values remain valid so required fields can use the dedicated required validator.
 *
 * @param control Form control containing the email address.
 * @returns `{completeEmailDomain: true}` when the domain has no complete suffix, otherwise `null`.
 */
export function completeEmailDomainValidator(control: AbstractControl<string | null>,): ValidationErrors | null {
  const value = control.value?.trim();
  if (!value) {
    return null;
  }

  const domain = value.slice(value.lastIndexOf('@') + 1);
  const domainLabels = domain.split('.');
  const hasCompleteDomain =
    value.includes('@') &&
    domainLabels.length >= 2 &&
    domainLabels.every((label) => label.length > 0);

  return hasCompleteDomain ? null : {completeEmailDomain: true};
}

/**
 * Validates that a date value is not in the future.
 *
 * The latest accepted value is the current day, so a birth date entered today is still valid.
 * The comparison is done on the local calendar day to avoid timezone drift for the
 * `YYYY-MM-DD` values emitted by the date input.
 *
 * Empty values remain valid so the validator can be combined with `Validators.required`.
 *
 * @param control Form control containing an ISO date string.
 * @returns `{futureDate: true}` when the date is after today, otherwise `null`.
 */
export function notInFutureDateValidator(control: AbstractControl<string | null>): ValidationErrors | null {
  const value = control.value?.trim();
  if (!value) {
    return null;
  }

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  let selectedDate: Date;
  if (isoMatch) {
    selectedDate = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  } else {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    selectedDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return selectedDate.getTime() > today.getTime() ? {futureDate: true} : null;
}

/**
 * Validates that an array-like control contains at least one non-blank entry.
 *
 * Use this for form arrays that represent uploaded file keys or similar list
 * values where placeholder empty strings should not satisfy the requirement.
 *
 * @param control Form array or control containing an array value.
 * @returns `{required: true}` when no non-blank entry exists, otherwise `null`.
 */
export function nonEmptyStringArrayValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!Array.isArray(value)) {
    return {required: true};
  }

  return value.some((entry) => typeof entry === 'string' && entry.trim().length > 0)
    ? null
    : {required: true};
}

/**
 * Marks every control in a form tree as touched and recomputes validity.
 *
 * Use this before submit handling when a form or nested sub-form should reveal all
 * currently failing validation rules at once.
 *
 * @param control Root form group, form array, or control to validate.
 */
export function markFormTreeTouched(control: AbstractControl): void {
  control.markAsTouched();
  control.updateValueAndValidity({onlySelf: true});

  if (control instanceof FormGroup || control instanceof FormArray) {
    Object.values(control.controls).forEach((childControl) => {
      markFormTreeTouched(childControl);
    });
  }
}

/**
 * Checks whether validation feedback should be visible for a control.
 *
 * Validation feedback is hidden for untouched pristine fields to avoid showing errors
 * before the user has interacted with the form. Pass `true` for `force` after a submit
 * attempt to reveal errors for the whole form.
 *
 * @param control Control whose validation state should be displayed.
 * @param force Whether validation feedback should be shown regardless of interaction state.
 * @returns `true` when the control is invalid and feedback should be rendered.
 */
export function shouldShowControlError(control: AbstractControl | null | undefined, force = false): boolean {
  return !!control && control.invalid && (force || control.touched || control.dirty);
}

/**
 * Returns a normalized validation error key and interpolation parameters for a control.
 *
 * Centralizing the error priority keeps form components consistent while still allowing
 * each component to translate the key in its own i18n scope.
 *
 * @param control Control whose first relevant validation error should be returned.
 * @returns A normalized error descriptor, or `null` when the control has no known error.
 */
export function getControlError(control: AbstractControl | null | undefined): FormControlError | null {
  const errors = control?.errors;
  if (!errors) {
    return null;
  }

  if (errors['atLeastOneRequired']) {
    return {key: 'atLeastOneRequired'};
  }

  if (errors['required'] || errors['blank']) {
    return {key: 'required'};
  }

  if (errors['personName']) {
    return {key: 'personName'};
  }

  if (errors['meaningfulText']) {
    return {key: 'meaningfulText'};
  }

  if (errors['houseNumber']) {
    return {key: 'houseNumber'};
  }

  if (errors['containsDigit']) {
    return {key: 'containsDigit'};
  }

  if (errors['postalCode']) {
    return {key: 'postalCode'};
  }

  if (errors['countryPostalCode']) {
    const {format} = errors['countryPostalCode'] as {format: CountryPostalCodeFormat};

    return {key: `postalCode.${format}`};
  }

  if (errors['futureDate']) {
    return {key: 'futureDate'};
  }

  if (errors['minlength']) {
    const {requiredLength, actualLength} = errors['minlength'] as {
      requiredLength: number;
      actualLength: number;
    };

    return {
      key: 'minlength',
      params: {
        requiredLength,
        actualLength,
      },
    };
  }

  if (errors['email']) {
    return {key: 'email'};
  }

  if (errors['completeEmailDomain']) {
    return {key: 'emailDomain'};
  }

  if (errors['phoneNumber']) {
    const phoneNumberError = errors['phoneNumber'] as {
      minDigits?: number;
      maxDigits?: number;
      actualDigits?: number;
    };

    return {
      key: 'phoneNumber',
      params: {
        minDigits: phoneNumberError.minDigits ?? phoneNumberMinDigits,
        maxDigits: phoneNumberError.maxDigits ?? phoneNumberMaxDigits,
        actualDigits: phoneNumberError.actualDigits ?? 0,
      },
    };
  }

  if (errors['maxlength']) {
    const {requiredLength, actualLength} = errors['maxlength'] as {
      requiredLength: number;
      actualLength: number;
    };

    return {
      key: 'maxlength',
      params: {
        requiredLength,
        actualLength,
      },
    };
  }

  return null;
}

/**
 * Resolves the translated validation message for a control.
 *
 * The helper keeps validation message rendering consistent across form
 * components. It returns an empty string while the control error should stay
 * hidden, for example before the user interacted with the field.
 *
 * @param control Control whose validation message should be resolved.
 * @param translate Translation function scoped to the current component.
 * @param force Whether validation feedback should be shown regardless of interaction state.
 * @param validationPrefix Translation key prefix for validation messages.
 * @returns Translated validation text or an empty string when nothing should be shown.
 */
export function translatedControlError(control: AbstractControl | null | undefined, translate: FormErrorTranslator, force = false, validationPrefix = 'validation'): string {
  if (!shouldShowControlError(control, force)) {
    return '';
  }

  const error = getControlError(control);

  return error ? translate(`${validationPrefix}.${error.key}`, error.params) : '';
}
